import { useEffect, useState } from 'react';
import {
  useAppContext,
  useConsId,
  useLinkFacebookFundraiser,
  usePersonalPageInfo,
} from '@frx/frx-react-context';
import { useToast } from '@chakra-ui/react';
import { useEventId } from './use-eventId';
import { isDev, isQA } from '../utils';

export type FacebookStatus = {
  type: string;
  message: string;
};

export const useFbFundraiser = () => {
  const frId = useEventId();
  const consId = useConsId();
  const [status, setStatus] = useState<FacebookStatus>();
  const {
    data: {
      authData: { user },
      eventData: { eventInfo, eventConfig },
    },
  } = useAppContext();
  const { data: personalPageInfo } = usePersonalPageInfo({
    cons_id: Number(user?.cons_id),
    fr_id: frId,
  });
  const toast = useToast();
  const { mutate: connectFacebookFundraiser, isLoading: linkFbIsLoading } =
    useLinkFacebookFundraiser();

  useEffect(() => {
    window.fbAsyncInit = function () {
      if (window.FB) {
        window.FB.init({
          appId: eventConfig?.facebookAppId || eventInfo?.facebookAppId,
          cookie: true,
          xfbml: true,
          version: 'v5.0',
        });
        window.FB.AppEvents.logPageView();
      }
    };
    ((d, s, id) => {
      let js: HTMLElement = {} as HTMLElement;
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      js = d.createElement(s);
      js.id = id;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      js.src = '//connect.facebook.net/en_US/sdk.js';
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      fjs?.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
  }, [eventConfig?.facebookAppId, eventInfo?.facebookAppId]);

  const handleFbLogin = () => {
    if (isQA || isDev) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return window.FB?.login(
      (res) => {
        const accessToken = res?.authResponse?.accessToken;
        if (accessToken) {
          window.FB?.api('/me/permissions', (permRes: any) => {
            const manageFundraisersPermission = permRes.data.find(
              (perm: any) => perm?.permission === 'manage_fundraisers'
            );

            if (!manageFundraisersPermission) {
              setStatus({
                type: 'permission_error',
                message:
                  'An error occurred attempting to connect to Facebook. Please try again later.',
              });
            } else if (manageFundraisersPermission?.status === 'declined') {
              setStatus({
                type: 'declined_manage_fundraisers',
                message:
                  'We need your permission to create a Facebook Fundraiser on your behalf.',
              });
            } else {
              connectFacebookFundraiser(
                {
                  fr_id: frId,
                  cons_id: consId as number,
                  user_access_token: accessToken,
                  name: `${
                    user?.screenName ||
                    `${user?.name?.first} ${user?.name?.last} fundraiser for ${eventInfo?.name}`
                  }`,
                  description: personalPageInfo?.personalPage.richText,
                },
                {
                  onSuccess: () => {
                    toast({
                      status: 'success',
                      duration: 3000,
                      title: 'Fundraiser created',
                    });
                  },
                  onError: (error) => {
                    toast({
                      status: 'error',
                      duration: 3000,
                      title: error?.message,
                    });
                  },
                }
              );
            }
          });
        }
      },
      {
        scope: 'manage_fundraisers',
      }
    );
  };

  return { handleFbLogin, linkFbIsLoading, status };
};
